/**
 * Meybra Global Quick Capture
 *
 * A floating action button (bottom-right) available on every coach-facing
 * page. The coach speaks or types one short observation. The AI:
 *   1. Transcribes the audio (Whisper) if voice input was used.
 *   2. Identifies which athlete the observation is about (name matching).
 *   3. Hands off to the existing Quick Development Update pipeline so the
 *      coach can review and approve the proposed profile evolution.
 *
 * Hard rules enforced here:
 *   - The AI never applies a change without explicit coach approval.
 *   - Low-confidence matches must trigger manual disambiguation.
 *   - Athletes with no baseline evaluation are routed to Full Evaluation.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Mic, MicOff, Loader2, Send, X, Search, ArrowLeft, Keyboard, Volume2,
  AlertCircle, UserCheck, FileText, Sparkles, ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import QuickUpdate from '@/components/QuickUpdate';
import { trackEvent } from '@/lib/telemetry';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- Stages -----
const STAGE_INPUT = 'input';            // pick voice or text
const STAGE_RECORDING = 'recording';    // capturing audio
const STAGE_TRANSCRIBING = 'transcribing';
const STAGE_TRANSCRIPT = 'transcript';  // editable text after voice
const STAGE_IDENTIFYING = 'identifying';
const STAGE_DISAMBIGUATE = 'disambiguate';
const STAGE_MANUAL = 'manual';
const STAGE_NO_BASELINE = 'no-baseline';
const STAGE_REVIEW = 'review';

const formatTimer = (ms) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const AthleteCard = ({ athlete, onPick, testIdPrefix = 'qc-athlete' }) => (
  <button
    type="button"
    onClick={() => onPick(athlete)}
    className="w-full flex items-center gap-3 p-3 rounded-sm bg-card border border-border/40 hover:border-primary/60 hover:bg-primary/5 transition-colors text-left"
    data-testid={`${testIdPrefix}-${athlete.id}`}
  >
    <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0 flex items-center justify-center">
      {athlete.photo_url ? (
        <img src={athlete.photo_url} alt={athlete.name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-sm font-bold text-muted-foreground">
          {(athlete.name || '?').slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{athlete.name}</p>
      <p className="text-xs text-muted-foreground truncate">
        {athlete.position || '—'}{athlete.age ? ` • ${athlete.age}y` : ''}
        {athlete.has_evaluation ? '' : ' • no baseline yet'}
      </p>
    </div>
    {!athlete.has_evaluation && (
      <span className="text-[10px] uppercase tracking-widest text-amber-400 shrink-0">Setup</span>
    )}
  </button>
);

const QuickCaptureFAB = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(STAGE_INPUT);
  const [observation, setObservation] = useState('');
  const [error, setError] = useState('');

  // Voice recording state
  const [recordSupported, setRecordSupported] = useState(true);
  const [recordingMs, setRecordingMs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Identification / review state
  const [identifyData, setIdentifyData] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [manualSearch, setManualSearch] = useState('');
  const [translationInfo, setTranslationInfo] = useState(null); // {detected_language, original_text} when Whisper translated

  const onlyCoach = user?.role === 'coach';

  // Detect MediaRecorder availability once on mount.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const ok = !!(navigator?.mediaDevices?.getUserMedia && window.MediaRecorder);
      setRecordSupported(ok);
    }
  }, []);

  // Reset everything whenever the modal closes — leaks across opens are
  // confusing (e.g. previous transcript bleeding into a new capture).
  const closeAll = () => {
    stopStream();
    setOpen(false);
    setStage(STAGE_INPUT);
    setObservation('');
    setError('');
    setIdentifyData(null);
    setSelectedAthlete(null);
    setManualSearch('');
    setRecordingMs(0);
    setTranslationInfo(null);
  };

  const openModal = () => {
    setOpen(true);
    trackEvent('quick_capture_opened', { source: 'fab' });
  };

  // ---------- Voice ----------
  const stopStream = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    setError('');
    if (!recordSupported) {
      toast.error('Voice not supported — please type your observation.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = handleRecordingStopped;
      mr.start();
      setStage(STAGE_RECORDING);
      const startedAt = Date.now();
      setRecordingMs(0);
      timerRef.current = setInterval(() => setRecordingMs(Date.now() - startedAt), 250);
    } catch (e) {
      setRecordSupported(false);
      setError('Microphone unavailable. Please type your observation instead.');
      setStage(STAGE_INPUT);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const handleRecordingStopped = async () => {
    // Release the mic immediately so the OS indicator turns off.
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
    chunksRef.current = [];
    if (!blob.size) {
      setError('Recording was empty. Try again or type your observation.');
      setStage(STAGE_INPUT);
      return;
    }
    trackEvent('quick_capture_audio_recorded', { bytes: blob.size, duration_ms: recordingMs });
    setStage(STAGE_TRANSCRIBING);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'capture.webm');
      const res = await axios.post(`${API}/coach/quick-capture/transcribe`, fd, {
        headers: { ...getAuthHeaders() },
      });
      const text = (res.data?.text || '').trim();
      if (!text) {
        setError('We could not transcribe that. Please try again or type the observation.');
        setStage(STAGE_INPUT);
        return;
      }
      setObservation(text);
      if (res.data?.translated) {
        setTranslationInfo({
          detected_language: res.data.detected_language,
          original_text: res.data.original_text,
        });
      } else {
        setTranslationInfo(null);
      }
      trackEvent('quick_capture_transcribed', {
        length: text.length,
        language: res.data?.detected_language || 'en',
        translated: !!res.data?.translated,
      });
      setStage(STAGE_TRANSCRIPT);
    } catch (e) {
      // Hard fallback per user requirement: voice fails → keep text path alive.
      const detail = e?.response?.data?.detail;
      setError(detail || 'Transcription failed. Please type your observation.');
      setStage(STAGE_INPUT);
    }
  };

  // ---------- Identify ----------
  const runIdentify = async (text) => {
    setStage(STAGE_IDENTIFYING);
    setError('');
    try {
      const res = await axios.post(
        `${API}/coach/quick-capture/identify`,
        { observation: text },
        { headers: getAuthHeaders() }
      );
      setIdentifyData(res.data);
      if (res.data.status === 'identified' && res.data.athlete) {
        handleAthletePicked(res.data.athlete, { identified: true });
      } else if (res.data.status === 'disambiguate') {
        trackEvent('quick_capture_failed_low_confidence', { reason: 'disambiguate' });
        setStage(STAGE_DISAMBIGUATE);
      } else {
        trackEvent('quick_capture_failed_low_confidence', { reason: 'no_match' });
        setStage(STAGE_MANUAL);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || 'Could not match this observation to an athlete.');
      setStage(STAGE_TRANSCRIPT);
    }
  };

  const handleAthletePicked = (athlete, opts = {}) => {
    setSelectedAthlete(athlete);
    if (opts.identified) {
      trackEvent('quick_capture_athlete_identified', { athlete_id: athlete.id, score: athlete.match_score });
    } else {
      trackEvent('quick_capture_manual_athlete_selected', { athlete_id: athlete.id });
    }
    if (!athlete.has_evaluation) {
      setStage(STAGE_NO_BASELINE);
      return;
    }
    trackEvent('quick_capture_analyzed', { athlete_id: athlete.id });
    setStage(STAGE_REVIEW);
  };

  // ---------- Manual search ----------
  const filteredManual = useMemo(() => {
    const list = identifyData?.athletes || [];
    const q = manualSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => (a.name || '').toLowerCase().includes(q));
  }, [identifyData, manualSearch]);

  // ---------- Render helpers ----------
  if (!onlyCoach) return null;

  return (
    <>
      {/* Floating action button — pill-shaped with icon + label so coaches
          immediately understand what it does. Sits above the Helpdesk chat
          widget (which lives at bottom-6 right-6) so the two don't overlap. */}
      <button
        type="button"
        onClick={openModal}
        className="fixed bottom-24 right-5 z-[115] flex items-center gap-2 pl-3 pr-4 h-12 sm:h-14 rounded-full bg-primary text-black shadow-2xl shadow-primary/30 hover:scale-[1.03] active:scale-95 transition-transform"
        title="Quick Capture — log a development observation"
        aria-label="Open Quick Capture"
        data-testid="quick-capture-fab"
      >
        <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/15 flex items-center justify-center shrink-0">
          <Mic size={16} className="text-black" />
        </span>
        <span className="font-heading uppercase tracking-widest text-[11px] sm:text-xs font-bold">
          Update Athlete
        </span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}
          data-testid="quick-capture-modal"
        >
          <div className="w-full sm:max-w-xl bg-background border border-border/60 rounded-t-lg sm:rounded-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-sm bg-primary/15 flex items-center justify-center shrink-0">
                  <Sparkles className="text-primary" size={16} />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-sm uppercase tracking-wider truncate">Quick Capture</p>
                  <p className="text-[11px] text-muted-foreground truncate">Log a development observation fast</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAll}
                className="p-1 text-muted-foreground hover:text-foreground"
                aria-label="Close Quick Capture"
                data-testid="quick-capture-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-4 space-y-4">
              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-300 text-xs rounded-sm" data-testid="quick-capture-error">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* ===== INPUT ===== */}
              {stage === STAGE_INPUT && (
                <div className="space-y-3" data-testid="quick-capture-stage-input">
                  {recordSupported && (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="w-full flex items-center gap-3 p-4 rounded-sm bg-primary/10 border border-primary/30 hover:bg-primary/15 transition-colors text-left"
                      data-testid="quick-capture-start-recording"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary text-black flex items-center justify-center shrink-0">
                        <Mic size={18} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">Record voice observation</p>
                        <p className="text-[11px] text-muted-foreground">
                          Speak in English, Português or Español — we translate to English automatically.
                        </p>
                      </div>
                    </button>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border/40" />
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or type it</span>
                    <div className="flex-1 h-px bg-border/40" />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Keyboard size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Write your observation</span>
                    </div>
                    <Textarea
                      value={observation}
                      onChange={(e) => setObservation(e.target.value)}
                      placeholder='e.g. "Antonella improved her first touch today and is scanning more before receiving."'
                      rows={4}
                      className="bg-transparent w-full"
                      data-testid="quick-capture-text-input"
                    />
                    <Button
                      type="button"
                      onClick={() => runIdentify(observation.trim())}
                      disabled={observation.trim().length < 10}
                      className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-11"
                      data-testid="quick-capture-submit-text"
                    >
                      <Send size={14} className="mr-2" /> Identify athlete
                    </Button>
                  </div>
                </div>
              )}

              {/* ===== RECORDING ===== */}
              {stage === STAGE_RECORDING && (
                <div className="space-y-4 text-center py-6" data-testid="quick-capture-stage-recording">
                  <div className="relative w-24 h-24 mx-auto">
                    <span className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <div className="relative w-24 h-24 rounded-full bg-red-500 flex items-center justify-center">
                      <Volume2 className="text-white" size={28} />
                    </div>
                  </div>
                  <p className="font-mono text-lg" data-testid="quick-capture-timer">{formatTimer(recordingMs)}</p>
                  <p className="text-xs text-muted-foreground">Recording… speak naturally.</p>
                  <Button
                    type="button"
                    onClick={stopRecording}
                    className="w-full bg-foreground text-background hover:bg-foreground/90 uppercase tracking-wider font-bold h-11"
                    data-testid="quick-capture-stop-recording"
                  >
                    <MicOff size={14} className="mr-2" /> Stop & transcribe
                  </Button>
                </div>
              )}

              {/* ===== TRANSCRIBING ===== */}
              {stage === STAGE_TRANSCRIBING && (
                <div className="text-center py-10 space-y-3" data-testid="quick-capture-stage-transcribing">
                  <Loader2 className="mx-auto animate-spin text-primary" size={32} />
                  <p className="text-sm text-muted-foreground">Transcribing your observation…</p>
                </div>
              )}

              {/* ===== TRANSCRIPT (editable) ===== */}
              {stage === STAGE_TRANSCRIPT && (
                <div className="space-y-3" data-testid="quick-capture-stage-transcript">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-primary" />
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">Edit transcript before analysis</span>
                  </div>
                  {translationInfo && (
                    <div
                      className="flex items-start gap-2 px-3 py-2 bg-primary/5 border border-primary/30 rounded-sm text-[11px] text-primary"
                      data-testid="quick-capture-translation-hint"
                    >
                      <span className="font-bold uppercase tracking-widest">Translated</span>
                      <span className="text-muted-foreground">
                        from {translationInfo.detected_language || 'your language'} → English. Original: &ldquo;{translationInfo.original_text}&rdquo;
                      </span>
                    </div>
                  )}
                  <Textarea
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    rows={5}
                    className="bg-transparent w-full"
                    data-testid="quick-capture-transcript-textarea"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setObservation(''); setStage(STAGE_INPUT); }}
                      className="flex-1"
                      data-testid="quick-capture-retry"
                    >
                      <ArrowLeft size={14} className="mr-2" /> Retry
                    </Button>
                    <Button
                      type="button"
                      onClick={() => runIdentify(observation.trim())}
                      disabled={observation.trim().length < 10}
                      className="flex-1 bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold"
                      data-testid="quick-capture-confirm-transcript"
                    >
                      <Send size={14} className="mr-2" /> Identify athlete
                    </Button>
                  </div>
                </div>
              )}

              {/* ===== IDENTIFYING ===== */}
              {stage === STAGE_IDENTIFYING && (
                <div className="text-center py-10 space-y-3" data-testid="quick-capture-stage-identifying">
                  <Loader2 className="mx-auto animate-spin text-primary" size={32} />
                  <p className="text-sm text-muted-foreground">Finding the athlete…</p>
                </div>
              )}

              {/* ===== DISAMBIGUATE ===== */}
              {stage === STAGE_DISAMBIGUATE && identifyData && (
                <div className="space-y-3" data-testid="quick-capture-stage-disambiguate">
                  <div className="flex items-center gap-2">
                    <UserCheck size={14} className="text-primary" />
                    <span className="text-xs uppercase tracking-widest">Which athlete did you mean?</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Multiple matches found in your roster.</p>
                  <div className="space-y-2">
                    {identifyData.candidates.map((a) => (
                      <AthleteCard key={a.id} athlete={a} onPick={(ath) => handleAthletePicked(ath, { identified: false })} testIdPrefix="qc-candidate" />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStage(STAGE_MANUAL)}
                    className="text-xs text-primary hover:underline w-full text-center mt-2"
                    data-testid="quick-capture-show-all"
                  >
                    None of these — pick from full list
                  </button>
                </div>
              )}

              {/* ===== MANUAL ===== */}
              {stage === STAGE_MANUAL && identifyData && (
                <div className="space-y-3" data-testid="quick-capture-stage-manual">
                  <div className="flex items-center gap-2">
                    <Search size={14} className="text-primary" />
                    <span className="text-xs uppercase tracking-widest">Pick the athlete manually</span>
                  </div>
                  <Input
                    value={manualSearch}
                    onChange={(e) => setManualSearch(e.target.value)}
                    placeholder="Search athletes…"
                    className="bg-transparent"
                    data-testid="quick-capture-manual-search"
                  />
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {filteredManual.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-6">
                        No athletes match that search.
                      </p>
                    )}
                    {filteredManual.map((a) => (
                      <AthleteCard key={a.id} athlete={a} onPick={(ath) => handleAthletePicked(ath, { identified: false })} testIdPrefix="qc-manual" />
                    ))}
                  </div>
                </div>
              )}

              {/* ===== NO BASELINE ===== */}
              {stage === STAGE_NO_BASELINE && selectedAthlete && (
                <div className="space-y-3" data-testid="quick-capture-stage-no-baseline">
                  <div className="flex items-start gap-2 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-200">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold">{selectedAthlete.name} has no baseline evaluation yet.</p>
                      <p className="text-xs text-amber-200/80 leading-relaxed">
                        Quick Updates need an existing profile to evolve. Run the first Full Evaluation, then come back here.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      closeAll();
                      navigate(`/coach/athletes/${selectedAthlete.id}`);
                    }}
                    className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-11"
                    data-testid="quick-capture-goto-full-eval"
                  >
                    <ClipboardList size={14} className="mr-2" /> Start Full Evaluation
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStage(identifyData?.status === 'disambiguate' ? STAGE_DISAMBIGUATE : STAGE_MANUAL)}
                    className="w-full"
                    data-testid="quick-capture-pick-other"
                  >
                    <ArrowLeft size={14} className="mr-2" /> Pick a different athlete
                  </Button>
                </div>
              )}

              {/* ===== REVIEW (Quick Update reuse) ===== */}
              {stage === STAGE_REVIEW && selectedAthlete && (
                <div className="space-y-3" data-testid="quick-capture-stage-review">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <UserCheck size={14} className="text-primary shrink-0" />
                      <span className="truncate">
                        Reviewing update for <span className="font-semibold text-foreground">{selectedAthlete.name}</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStage(identifyData?.candidates?.length ? STAGE_DISAMBIGUATE : STAGE_MANUAL)}
                      className="text-primary hover:underline shrink-0"
                      data-testid="quick-capture-change-athlete"
                    >
                      change
                    </button>
                  </div>
                  <QuickUpdate
                    athleteId={selectedAthlete.id}
                    athleteName={selectedAthlete.name}
                    coachId={user?.coach_id || user?.id}
                    currentEval={null}
                    initialObservation={observation}
                    autoAnalyze
                    source="quick_capture"
                    onBack={null}
                    onApplied={() => {
                      trackEvent('quick_capture_applied', { athlete_id: selectedAthlete.id });
                      toast.success('Quick Capture applied — profile recompiling.');
                      closeAll();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickCaptureFAB;
